import React, {
  createContext, useContext, useEffect, useLayoutEffect, useMemo, useState, useCallback,
} from 'react';
import { AllContent, ContentMap, Lang, LANGS, DEFAULT_LANG } from '../types';
import { PublicApi } from '../utils/apiClient';
import bundled from '../data/content.json';

const BUNDLED = bundled as unknown as AllContent;

/** useLayoutEffect in the browser (runs before paint), useEffect during prerender/no-DOM. */
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/** Language the non-prefixed routes ('/', legal pages) are prerendered in — the SEO
 *  x-default. MUST match scripts/prerender.js so hydration matches on first paint. */
const PRERENDER_LANG: Lang = 'en';

/** Explicit lang-prefixed route (/de,/en,/it), else null. */
function urlLang(): Lang | null {
  try {
    const seg = (typeof window !== 'undefined' ? window.location.pathname : '')
      .split('/')[1]?.toLowerCase();
    if (seg && (LANGS as string[]).includes(seg)) return seg as Lang;
  } catch { /* ignore */ }
  return null;
}

/** The visitor's actual preference: stored choice, else browser language, else default. */
function preferredLang(): Lang {
  try {
    const stored = localStorage.getItem('rts_lang');
    if (stored && (LANGS as string[]).includes(stored)) return stored as Lang;
  } catch { /* ignore */ }
  const rawNav = typeof navigator !== 'undefined' ? (navigator.language || '') : '';
  const nav = rawNav.slice(0, 2).toLowerCase();
  if ((LANGS as string[]).includes(nav)) return nav as Lang;
  return DEFAULT_LANG;
}

/** First render: a lang-prefixed URL wins; otherwise the prerendered language so
 *  hydration matches. The real preference is applied after mount (see effect below). */
function initialLang(): Lang {
  return urlLang() ?? PRERENDER_LANG;
}

interface I18nCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  /** plain-text translation with bundled + key fallback */
  t: (key: string) => string;
  /** whether remote content finished loading (API or fallback) */
  ready: boolean;
  allContent: AllContent;
}

const Ctx = createContext<I18nCtx | null>(null);

export const LanguageProvider: React.FC<{ children: React.ReactNode; forcedLang?: Lang }> = ({
  children, forcedLang,
}) => {
  const [lang, setLangState] = useState<Lang>(() => forcedLang || initialLang());
  const [allContent, setAllContent] = useState<AllContent>(BUNDLED);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (forcedLang) setLangState(forcedLang);
  }, [forcedLang]);

  // After hydration, apply the visitor's real language on non-prefixed routes
  // ('/', legal). useLayoutEffect => the switch happens BEFORE the browser paints,
  // so the page is painted once in the final language (no visible flash, no CLS).
  // Hydration still matches because the initial render uses the prerendered language.
  useIsomorphicLayoutEffect(() => {
    if (forcedLang || urlLang()) return;
    const pref = preferredLang();
    setLangState((cur) => (pref !== cur ? pref : cur));
  }, [forcedLang]);

  useEffect(() => {
    let cancelled = false;
    PublicApi.allContent()
      .then((remote) => {
        if (cancelled || !remote) return;
        // Merge remote over bundled so any missing key still resolves.
        const merged: AllContent = { de: {}, en: {}, it: {} } as AllContent;
        LANGS.forEach((l) => {
          merged[l] = { ...(BUNDLED[l] || {}), ...(remote[l] || {}) };
        });
        setAllContent(merged);
      })
      .catch(() => { /* keep bundled fallback */ })
      .finally(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, []);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try { localStorage.setItem('rts_lang', l); } catch { /* ignore */ }
  }, []);

  const t = useCallback((key: string): string => {
    const map: ContentMap = allContent[lang] || {};
    if (map[key] != null) return map[key];
    const fb = BUNDLED[lang] || {};
    if (fb[key] != null) return fb[key];
    return key;
  }, [allContent, lang]);

  const value = useMemo<I18nCtx>(() => ({ lang, setLang, t, ready, allContent }),
    [lang, setLang, t, ready, allContent]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useI18n(): I18nCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useI18n must be used within LanguageProvider');
  return ctx;
}
